import axios from 'axios';

const API_URL = 'https://gmk-web-api.chronopulse.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Default config for all axios requests
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const login = async (username, password) => {
  const response = await api.post('/users/login', { username, password });
  return response.data;
};

// Events APIs
export const getEvents = async () => {
  const response = await api.get('/events');
  var eventList = Array.isArray(response.data) ? response.data : [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  eventList.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    const isAFuture = dateA >= today;
    const isBFuture = dateB >= today;

    if (isAFuture && !isBFuture) return -1;
    if (!isAFuture && isBFuture) return 1;

    return Math.abs(dateA - today) - Math.abs(dateB - today);
  });

  return eventList
};

export const getEventById = async (id) => {
  const response = await api.get(`/events/${id}`);
  return response.data;
};

export const createEvent = async (formData) => {
  const response = await api.post('/events', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const updateEvent = async (id, formData) => {
  const response = await api.put(`/events/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deleteEvent = async (id) => {
  const response = await api.delete(`/events/${id}`);
  return response.data;
};

// Slider APIs
export const getSliderItems = async () => {
  const response = await api.get('/slider');
  return response.data;
};

export const getSliderItemById = async (id) => {
  const response = await api.get(`/slider/${id}`);
  return response.data;
};

export const createSliderItem = async (formData) => {
  const response = await api.post('/slider', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  getSliderItems();
  return response.data;
};

export const updateSliderItem = async (id, formData) => {
  const response = await api.put(`/slider/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  getSliderItems();
  return response.data;
};

export const deleteSliderItem = async (id) => {
  const response = await api.delete(`/slider/${id}`);
  getSliderItems();
  return response.data;
};

// News APIs
export const getNewsItems = async () => {
  const response = await api.get('/news');
  return response.data;
};

export const getLatestNews = async (limit = 5) => {
  const response = await api.get(`/news/latest?limit=${limit}`);
  return response.data;
};

export const getNewsItemById = async (id) => {
  const response = await api.get(`/news/${id}`);
  return response.data;
};

export const createNewsItem = async (formData) => {
  const response = await api.post('/news', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  getLatestNews();
  return response.data;
};

export const updateNewsItem = async (id, formData) => {
  const response = await api.put(`/news/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  getLatestNews();
  return response.data;
};

export const deleteNewsItem = async (id) => {
  const response = await api.delete(`/news/${id}`);
  getLatestNews();
  return response.data;
};

// History APIs
export const getHistoryItems = async () => {
  const response = await api.get('/history');
  return response.data;
};

export const getHistoryItemById = async (id) => {
  const response = await api.get(`/history/${id}`);
  return response.data;
};

export const createHistoryItem = async (formData) => {
  const response = await api.post('/history', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const updateHistoryItem = async (id, formData) => {
  const response = await api.put(`/history/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deleteHistoryItem = async (id) => {
  const response = await api.delete(`/history/${id}`);
  return response.data;
};

// Message APIs
export const getAllMessages = async () => {
  const response = await api.get('/messages');
  return response.data;
};

export const getMessage = async (id) => {
  const response = await api.get(`/messages/${id}`);
  return response.data;
};

export const updateMessageStatus = async (id, data) => {
  const response = await api.put(`/messages/${id}/status`, data);
  return response.data;
};

export const deleteMessage = async (id) => {
  const response = await api.delete(`/messages/${id}`);
  return response.data;
};

// Contact APIs
export const getAllContacts = async () => {
  const response = await api.get('/contact');
  return response.data;
};

export const getContact = async (id) => {
  const response = await api.get(`/contact/${id}`);
  return response.data;
};

export const createContact = async (data) => {
  const response = await api.post('/contact', data);
  return response.data;
};

export const updateContact = async (id, data) => {
  const response = await api.put(`/contact/${id}`, data);
  return response.data;
};

export const deleteContact = async (id) => {
  const response = await api.delete(`/contact/${id}`);
  return response.data;
};

// Regulation APIs
export const getRegulationCategories = async () => {
  const response = await api.get('/regulations/categories');
  return response.data;
};

export const createRegulationCategory = async (data) => {
  const response = await api.post('/regulations/categories', data);
  return response.data;
};

export const updateRegulationCategory = async (id, data) => {
  const response = await api.put(`/regulations/categories/${id}`, data);
  return response.data;
};

export const deleteRegulationCategory = async (id) => {
  const response = await api.delete(`/regulations/categories/${id}`);
  return response.data;
};

export const getRegulations = async () => {
  const response = await api.get('/regulations/regulations');
  return response.data;
};

export const createRegulation = async (formData) => {
  const response = await api.post('/regulations/regulations', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateRegulation = async (id, formData) => {
  const response = await api.put(`/regulations/regulations/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteRegulation = async (id) => {
  const response = await api.delete(`/regulations/regulations/${id}`);
  return response.data;
};

// Community APIs
export const getCommunityMembers = async (params) => {
  const response = await api.get('/community', { params });
  return response.data;
};

export const getCommunityMemberById = async (id) => {
  const response = await api.get(`/community/${id}`);
  return response.data;
};

export const updateCommunityMember = async (id, data) => {
  const response = await api.put(`/community/${id}`, data);
  return response.data;
};

export const deleteCommunityMember = async (id) => {
  const response = await api.delete(`/community/${id}`);
  return response.data;
};

// Drivers APIs
export const getDriverStats = async () => {
  const response = await api.get('/drivers/stats');
  return response.data;
};

export const getDrivers = async (params) => {
  const response = await api.get('/drivers', { params });
  return response.data;
};

export const getDriverById = async (id) => {
  const response = await api.get(`/drivers/${id}`);
  return response.data;
};

export const updateDriverStatus = async (id, status) => {
  const response = await api.put(`/drivers/${id}/status`, { status });
  return response.data;
};

export const updateDriver = async (id, formData) => {
  const response = await api.put(`/drivers/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteDriver = async (id) => {
  const response = await api.delete(`/drivers/${id}`);
  return response.data;
};

export default api;
